import tippy from "tippy.js";

import { Bold } from "@tiptap/extension-bold";
import { BulletList } from "@tiptap/extension-bullet-list";
import { Document } from "@tiptap/extension-document";
import { ListItem } from "@tiptap/extension-list-item";
import Mention, { MentionNodeAttrs } from "@tiptap/extension-mention";
import { TaskItem } from "@tiptap/extension-task-item";
import { TaskList } from "@tiptap/extension-task-list";
import { Paragraph } from "@tiptap/extension-paragraph";
import { Text } from "@tiptap/extension-text";
import {
  Editor,
  Extension,
  generateHTML,
  JSONContent,
  Range,
  ReactRenderer,
} from "@tiptap/react";

import { MentionList } from "@/components/MentionList";

// eslint-disable-next-line @typescript-eslint/consistent-type-definitions
type TiptapOptions = {
  disableBold?: boolean;
};

// The mention controller + rendered mention node, shared by every editor that
// supports tagging regardless of which formatting extensions it ships with.
const createMentionExtensions = (): Extension[] => {
  const suggestion = {
      command: ({
        editor,
        props,
        range,
      }: {
        editor: Editor;
        props: MentionNodeAttrs;
        range: Range;
      }) => {
        const { type, id, label } = props as MentionNodeAttrs & {
          type: string;
        };

        editor
          .chain()
          .focus()
          .insertContentAt(range, {
            type,
            attrs: {
              id,
              label,
            },
          })
          .run();
      },
      render: () => {
        // @ts-expect-error - TODO: Fix this.
        let component;
        // @ts-expect-error - TODO: Fix this.
        let popup;

        return {
          // @ts-expect-error - TODO: Fix this.
          onStart: (props) => {
            component = new ReactRenderer(MentionList, {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
              props,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
              editor: props.editor,
            });

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (!props.clientRect) {
              return;
            }

            popup = tippy("body", {
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
              getReferenceClientRect: props.clientRect,
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
              appendTo: (props.editor.view.dom.parentElement ??
                document.body) as HTMLElement,
              content: component.element,
              showOnCreate: true,
              interactive: true,
              trigger: "manual",
              placement: "bottom-start",
            });
          },

          // @ts-expect-error - TODO: Fix this.
          onUpdate(props) {
            // @ts-expect-error - TODO: Fix this.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            component.updateProps(props);

            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (!props.clientRect) {
              return;
            }

            // @ts-expect-error - TODO: Fix this.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            popup[0].setProps({
              // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-assignment
              getReferenceClientRect: props.clientRect,
            });
          },

          // @ts-expect-error - TODO: Fix this.
          onKeyDown(props) {
            // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
            if (props.event.key === "Escape") {
              // @ts-expect-error - TODO: Fix this.
              // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
              popup[0].hide();

              return true;
            }

            // @ts-expect-error - TODO: Fix this.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access, @typescript-eslint/no-unsafe-return
            return component.ref?.onKeyDown(props.event);
          },

          onExit() {
            // @ts-expect-error - TODO: Fix this.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            popup[0].destroy();
            // @ts-expect-error - TODO: Fix this.
            // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
            component.destroy();
          },
        };
      },
    };

    const MentionController = Mention.extend({
      name: "mentionController",
    }).configure({
      deleteTriggerWithBackspace: true,
      suggestion,
    });

    const UserMention = Mention.extend({
      name: "user-mention",
      addAttributes() {
        return {
          id: { default: null },
          label: { default: null },
        };
      },
      renderHTML({ node }) {
        return [
          "a",
          {
            class: "mention",
            // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
            "data-id": node.attrs.id,
            "data-type": "user-mention",
          },
          // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
          `@${node.attrs.label}`,
        ];
      },
    });

  return [MentionController, UserMention] as Extension[];
};

export const tiptap = {
  generateBaseExtensions: (options?: TiptapOptions): Extension[] => {
    return [
      BulletList,
      Document,
      ListItem,
      Paragraph,
      TaskItem.configure({ nested: true }),
      TaskList,
      Text,
      ...(!options?.disableBold ? [Bold] : []),
    ] as Extension[];
  },

  generateMentionExtensions: (options?: TiptapOptions): Extension[] => {
    return [
      ...tiptap.generateBaseExtensions(options),
      ...createMentionExtensions(),
    ];
  },

  // Plain text + @mentions only — no bold, lists, or other formatting nodes.
  generateMentionOnlyExtensions: (): Extension[] => {
    return [
      Document,
      Paragraph,
      Text,
      ...createMentionExtensions(),
    ] as Extension[];
  },

  isEmptyFn: (value: string) => {
    const strippedValue = value.replace(/ /g, "").replace(/<p><\/p>/g, "");
    return strippedValue === "<p></p>" || strippedValue === "";
  },

  // ! This needs to be kept in sync with the extensions in `RichTextEditorInputWithMention`.
  jsonToHtmlFn: () => {
    const extensions = tiptap.generateMentionExtensions();
    return (json: JSONContent) => generateHTML(json, extensions);
  },

  stringToHtmlFn: (value: string) => {
    return "<p>" + value + "</p>";
  },
};
